import { Stack, Select, MenuItem, Button, OutlinedInput, InputAdornment, Chip, Typography } from "@mui/material";
import React, { useCallback, useEffect, useMemo } from "react";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import GroupUserPanel from "../../../../Components/Panels/GroupUserPanel/group-user-panel";
import { useToggle } from "../../../../Utils/Hooks/useToggle";
import ManageRolesPanel from "../../../../Components/Panels/ManageRolesPanel/manage-roles-panel";
import { ModalContext } from "../../../../Components/Modals";
import ListPageHeader, {
  ListPageHeaderGroupActions,
  ListPageHeaderRight,
} from "../../../../Components/Misc/ListPageHeader";
import { GridRowsProp, GridColDef, GridRowParams, GridActionsCellItem } from "@mui/x-data-grid";
import { userService } from "../../../../Core/Api/services";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import TableSkeleton from "../../../../Components/Table/TableSkeleton";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";
import { GroupAdvancedInfos } from "../../../../Core/Api/Types/group-advanced-infos";
import UserGroupChip from "../../../../Components/Misc/Chips/UserGroupChip/user-group-chip";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import { DbUserGroupInfos } from "../../../../Core/Api/Types/db-user-group-infos";
import ModalAddGroup, { ModalAddGroupElementProps } from "../../../../Components/Modals/ModalAddGroup/modal-add-group";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import GlobalConst from "../../../../Core/Utils/Constants/globals";
import TableCellTypography from "../../../../Components/Table/TableCellTypography/table-cell-typography";
import { produce } from "immer";
import { AclWorkflowAccessLevel } from "../../../../Core/Api/Types/acl-workflow-access-level";
import { AclAssetAccessLevel } from "../../../../Core/Api/Types/acl-asset-access-level";
import { DbUserGroupRoleInfos } from "../../../../Core/Api/Types/db-user-group-role-infos";
import { UserHasAcl } from "../../../../Utils/helpers";
import { useOutletContext } from "react-router";
import { SettingsOutletContextType } from "../settings";
import { useTranslation } from "react-i18next";
import TableUuids from "../../../../Components/Table/table-uuids";

export interface GroupPermissionsRows {
  id: number;
  usersCount: number | null;
  groupName?: string | null;
  permissions: AclTypes[] | null;
  permissionsNumber: string;
  groupFull: GroupAdvancedInfos;
  roleId?: number | null;
}

export interface GroupPermissionsProps {
  rows: GroupPermissionsRows[];
}

function SetingsRolesAndPermissions() {
  const { t } = useTranslation();
  // Filters
  const [roleFilter, setRoleFilter] = React.useState(-2);
  const [providerFilter, setProviderFilter] = React.useState(-1);
  const [textFilter, setTextFilter] = React.useState("");
  // Loading
  const [isLoading, setIsLoading] = React.useState(true);
  // Table rows
  const [rows, setRows] = React.useState<GridRowsProp>([]);
  // Roles
  const [roles, setRoles] = React.useState<DbUserGroupRoleInfos[]>([]);

  const [selectedItemIds, setSelectedItems] = React.useState<number[]>([]);
  const { setSelectionMode, selectionMode } = useOutletContext<SettingsOutletContextType>();

  // Group id for side panel
  const [groupSelected, setGroupSelected] = React.useState<GroupPermissionsRows>();
  const enqueueCustomSnackbar = useCustomSnackbar();

  const { active: groupUserPanel, toggle: toggleGroupUserPanel } = useToggle(false);
  const { active: manageRolesPanel, toggle: toggleManageRolesPanel } = useToggle(false);

  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const apiGrid = useGridApiRef();

  const groupsNamesAndIds: Record<number, string> = useMemo(() => {
    const output: Record<number, string> = {};
    rows.forEach((row) => {
      const rowId = (row as GroupPermissionsRows).id;
      const rowName = (row as GroupPermissionsRows).groupName!;
      output[rowId] = rowName;
    });
    return output;
  }, [rows]);

  const handleOpenModalNewGroup = React.useCallback(() => {
    if (!openModal) return;
    const onClose: ModalAddGroupElementProps["onClose"] = (data) => {};

    openModal<ModalAddGroupElementProps>(ModalAddGroup, {
      onClose,
      onConfirmModalCreateGroup,
    });
  }, [openModal]);

  const handleDeleteClick = useCallback(
    (id: number) => {
      userService.DeleteGroup(id).then(function (response) {
        if (response.success) {
          enqueueCustomSnackbar(t("alert:success.groupDeleted"), "success");
          setRows(rows.filter((x) => x.id !== id));
        }
      });
    },
    [rows]
  );

  const onConfirmModalCreateGroup = (group: DbUserGroupInfos) => {
    setRows(
      produce((draft) => {
        draft.unshift({
          id: group.id,
          usersCount: 0,
          groupName: group.name,
          permissions: group.acls,
          permissionsNumber:
            (group.acls ? group.acls?.length.toString() : "0") + "/" + Object.keys(AclTypes).length.toString(),
          groupFull: {
            id: group.id,
            isNew: true,
            usersCount: 0,
            group: group,
            entity: null,
            labProviders: null,
          },
          roleId: group.roleId,
        });
      })
    );
  };

  const onConfirm = (group: DbUserGroupInfos) => {
    setRows(
      rows.map((row) => {
        if (row.id === group.id)
          return {
            id: row.id,
            usersCount: row.usersCount,
            groupName: group.name,
            permissions: group.acls,
            permissionsNumber:
              (group.acls ? group.acls.length.toString() : "0") + "/" + Object.keys(AclTypes).length.toString(),
            groupFull: {
              usersCount: row.groupFull.usersCount,
              group: group,
              entity: row.groupFull.entity,
              labProviders: row.groupFull.labProviders,
            },
            roleId: group.roleId,
          };
        else return row;
      })
    );
    toggleGroupUserPanel();
  };

  const toggleGroupPanel = useCallback(
    (e: GridRowParams) => {
      if (manageRolesPanel) toggleManageRolesPanel();
      setGroupSelected(e.row);
      if (!groupUserPanel) toggleGroupUserPanel();
    },
    [manageRolesPanel, groupUserPanel]
  );

  const openManageRolesPanel = useCallback(() => {
    if (groupUserPanel) {
      toggleGroupUserPanel();
      setGroupSelected(undefined);
    }

    toggleManageRolesPanel();
  }, [groupUserPanel, manageRolesPanel]);

  // #region Grid definition
  const GroupPermissionsColumns: GridColDef[] = useMemo(() => {
    return [
      {
        headerName: t("common:name"),
        field: "groupName",
        flex: CellSize.large,
        minWidth: CellSize.large,
        renderCell: (data: any) => <TableCellTypography data={data.value} />,
      },
      {
        headerName: t("table:headerName.role"),
        field: "roleId",
        flex: CellSize.medium,
        minWidth: CellSize.medium,
        type: "singleSelect",
        valueGetter: (data: any, row: any) => roles.find((x) => x.id === row?.roleId)?.name || "No role",
        valueOptions: () => roles.map((x) => x.name ?? "No role") || [],
        renderCell: (data: any) => <UserGroupChip roleId={data?.row?.roleId} roles={roles} />,
      },
      {
        headerName: t("common:description"),
        field: "groupFull.group.description",
        flex: CellSize.large,
        minWidth: CellSize.large,
        valueGetter: (data: any, row: any) => row?.groupFull.group.description,
        renderCell: (data: any) => (
          <Typography variant="body1" color="text.secondary">
            {data.value}
          </Typography>
        ),
      },
      {
        headerName: t("common:users"),
        field: "usersCount",
        type: "number",
        flex: CellSize.xsmall,
        minWidth: CellSize.xsmall,
        renderCell: (data: any) => <Chip size="small" label={data.value || 0} color="primary" />,
      },
      {
        headerName: t("table:headerName.workflowAccess"),
        field: "groupFull.group.aclWorkflowAccessLevel",
        flex: CellSize.large,
        minWidth: CellSize.large,
        type: "singleSelect",
        valueOptions: () => Object.keys(AclWorkflowAccessLevel).map((x) => x),
        valueGetter: (data: any, row: any) => {
          if (!row) return;
          return row?.groupFull.group.aclWorkflowAccessLevel;
        },
        renderCell: (data: any) => <Chip size="small" label={data.value} color="default" />,
      },
      {
        headerName: t("table:headerName.assetAccess"),
        // field: "groupFull.group.aclAssetAccessLevel",
        field: "groupFull",
        flex: CellSize.small,
        minWidth: CellSize.small,
        type: "singleSelect",
        valueOptions: () => Object.keys(AclAssetAccessLevel).map((x) => x),
        valueGetter: (data: any, row: any) => row?.groupFull.group.aclAssetAccessLevel,
        renderCell: (data: any) => <Chip size="small" label={data.value} color="default" />,
      },
      {
        headerName: t("common:permissions"),
        field: "permissionsNumber",
        flex: CellSize.small,
        minWidth: CellSize.small,
        renderCell: (data: any) => {
          const [count, totalCount] = data.value.split("/");
          return (
            <Typography variant="body1">
              <Typography fontWeight={500} component="span">
                {count}
              </Typography>
              <Typography variant="body2" component="span" color="text.secondary" sx={{ ml: 0.5 }}>
                / {totalCount}
              </Typography>
            </Typography>
          );
        },
      },
      {
        headerName: t("table:headerName.titleFilters"),
        field: "groupFull.group.filterTitleIds",
        flex: CellSize.small,
        minWidth: CellSize.small,
        valueGetter: (data: any, row: any) => row?.groupFull.group.filterTitleIds?.length ?? 0,
        renderCell: (data: any) => (data.value > 0 ? <Chip size="small" label={data.value} color="default" /> : null),
      },
      {
        headerName: t("table:headerName.providerLinks"),
        field: "groupFull.labProviders",
        flex: CellSize.large,
        minWidth: CellSize.large,
        valueGetter: (data: any, row: any) => row?.groupFull.labProviders?.map((provider) => provider.labName),
        renderCell: (data: any) =>
          data.value ? (
            <Stack sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }} direction={"row"}>
              {data.value.map((x: string) => (
                <Chip label={x} key={x} size="small" />
              ))}
            </Stack>
          ) : (
            ""
          ),
      },
      {
        headerName: "",
        field: "actions",
        type: "actions",
        align: "right",
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem
            icon={<GroupsOutlinedIcon />}
            label={t("table:cellLabel.editPermissions")}
            onClick={() => setGroupSelected(params.row)}
            showInMenu
          />,
          <GridActionsCellItem
            icon={<DeleteOutlinedIcon color="error" />}
            label={t("common:delete")}
            color="error"
            onClick={() => handleDeleteClick(params.row["id"])}
            showInMenu
          />,
        ],
      },
    ];
  }, [roles]);
  // #endregion

  useEffect(() => {
    if (groupSelected?.id && !groupUserPanel) {
      toggleGroupUserPanel();
      if (manageRolesPanel) toggleManageRolesPanel();
    }
  }, [groupSelected?.id]);

  React.useEffect(() => {
    userService.GetGroupsListForRolesAndPermissions().then((response) => {
      if (response) setRows(response.rows);

      setIsLoading(false);
    });

    userService.GetGroupRolesList().then((response) => {
      if (response.groupRoles) setRoles(response.groupRoles);
    });
  }, []);

  React.useEffect(() => {
    setSelectionMode({ type: "roles-and-permissions", selection: selectedItemIds });
  }, [selectedItemIds]);

  React.useEffect(() => {
    if (selectionMode.selection.length === 0 && selectedItemIds.length !== 0) setSelectedItems([]);
  }, [selectionMode]);

  const additionalFilters = (
    <>
      <Select
        displayEmpty
        IconComponent={ExpandMoreRoundedIcon}
        startAdornment={<PeopleAltOutlinedIcon />}
        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        value={roleFilter}
        onChange={(e, v) => setRoleFilter(e.target.value as number)}
      >
        <MenuItem value={-2}>{t("table:headerName.role")}</MenuItem>
        <MenuItem value={-1} selected>
          {t("menu:item.noRole")}
        </MenuItem>
        {roles.map((role) => (
          <MenuItem key={role.id} value={role.id}>
            {role.name}
          </MenuItem>
        ))}
      </Select>
      <Select
        displayEmpty
        IconComponent={ExpandMoreRoundedIcon}
        startAdornment={<PeopleAltOutlinedIcon />}
        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        value={providerFilter}
        onChange={(e, v) => setProviderFilter(e.target.value as number)}
      >
        <MenuItem value={-1}>{t("common:provider")}</MenuItem>
        {rows
          .filter((p) => p.groupFull.labProviders !== null && p.groupFull.labProviders.length > 0)
          .flatMap((p) => p.groupFull.labProviders)
          .map((provider) => (
            <MenuItem key={provider.id} value={provider.id}>
              {provider.labName}
            </MenuItem>
          ))}
      </Select>
    </>
  );

  const additionalActions = (
    <>
      <Button
        startIcon={<SettingsOutlinedIcon />}
        onClick={openManageRolesPanel}
        disabled={!UserHasAcl(AclTypes.aclGroupRoleManage)}
      >
        {t("button:manageRoles")}
      </Button>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleOpenModalNewGroup}
        disabled={!UserHasAcl(AclTypes.aclBackofficeUserCreate)}
      >
        {t("common:newGroup")}
      </Button>
    </>
  );

  return (
    <>
      <DataTable
        loading={isLoading}
        tableKey={TableUuids.SETTINGS_ROLE_AND_PERMISSIONS}
        columns={GroupPermissionsColumns}
        rows={rows.filter(
          (x) =>
            (roleFilter === -2 || x.roleId === roleFilter || (roleFilter === -1 && x.roleId === null)) &&
            (textFilter === "" ||
              x.groupName?.toString().includesAllWords(textFilter) ||
              x.groupFull.group.description?.toString().includesAllWords(textFilter) ||
              x.groupFull.labProviders?.filter((y) => y.labName.includesAllWords(textFilter)).length > 0) &&
            (providerFilter === -1 || x.groupFull.labProviders?.filter((y) => y.id === providerFilter).length > 0)
        )}
        getRowHeight={() => "auto"}
        pagination={true}
        initialState={{
          sorting: {
            sortModel: [{ field: "createdAt", sort: "desc" }],
          },
        }}
        onRowClick={toggleGroupPanel}
        apiRef={apiGrid}
        selectedRows={selectedItemIds}
        setSelectedRows={setSelectedItems}
        checkboxSelection={true}
        disableRowSelectionOnClick
        additionalActions={additionalActions}
        additionalFilters={additionalFilters}
        initialColumnVisibility={{
          ["groupFull.group.filterTitleIds"]: false,
          ["groupFull.labProviders"]: false,
        }}
      />
      {groupSelected && (
        <GroupUserPanel
          open={groupUserPanel}
          onClose={toggleGroupUserPanel}
          groupRow={groupSelected}
          onConfirm={onConfirm}
          groupsNamesAndIds={groupsNamesAndIds}
          roles={roles}
        />
      )}

      {manageRolesPanel && (
        <ManageRolesPanel
          open={manageRolesPanel}
          onClose={toggleManageRolesPanel}
          onCloseUserRole={toggleManageRolesPanel}
        />
      )}
    </>
  );
}

export default SetingsRolesAndPermissions;
